import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Zap, Calendar, ArrowRight, ExternalLink, CheckCircle, AlertTriangle } from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS } from "../lib/status";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const customerAxios = () => {
  const session = localStorage.getItem("lf_customer_session") || "";
  return axios.create({
    withCredentials: true,
    headers: session ? { "X-Customer-Session": session } : {},
  });
};

const EUR_RATE = 0.92;
const fmt = (n, c) => c === "eur" ? `€${(n * EUR_RATE).toFixed(0)}` : `$${(n || 0).toFixed(0)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await customerAxios().get(`${API}/customer/projects`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("lf_customer_session");
        navigate("/customer/login");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    try { await customerAxios().post(`${API}/customer/logout`); } catch (err) { console.warn("Customer logout call failed (continuing client-side):", err); }
    localStorage.removeItem("lf_customer_session");
    localStorage.removeItem("lf_customer_email");
    navigate("/customer/login");
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-50" data-testid="customer-dashboard">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-zinc-950 rounded-lg flex items-center justify-center"><Zap size={16} className="text-white" /></div>
        <div className="h-3 w-32 bg-zinc-200 rounded animate-pulse" />
      </header>
      <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-zinc-400" /></div>
    </div>
  );

  const projects = data?.projects || [];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="customer-dashboard">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-zinc-950 rounded-lg flex items-center justify-center"><Zap size={16} className="text-white" /></div>
          <div>
            <div className="font-bold text-zinc-900 text-sm">Your Projects</div>
            <div className="text-xs text-zinc-500" data-testid="customer-email">{data?.email}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="btn-customer-logout"
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
          <LogOut size={14} /> Sign out
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {projects.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-3xl p-12 text-center" data-testid="empty-projects">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap size={24} className="text-zinc-400" />
            </div>
            <div className="font-bold text-zinc-950 text-lg">No projects yet</div>
            <p className="text-sm text-zinc-600 mt-2">Submit your idea and we'll generate a quote in minutes.</p>
            <Link to="/build" data-testid="btn-start-project"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800">
              Start a project <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="projects-list">
            {projects.map((p) => {
              const proposal = p.proposal;
              const price = proposal?.delivery_adjusted_price_usd;
              const overdue = p.due_date && new Date(p.due_date) < new Date() && p.status !== "delivered";
              return (
                <Link key={p.project_id} to={`/dashboard/projects/${p.project_id}`} data-testid={`customer-project-${p.project_id}`}
                  className="bg-white border border-zinc-200 rounded-3xl p-6 hover:border-zinc-400 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[p.status] || "bg-zinc-100"}`}>
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                        {p.status === "final_review" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">
                            <AlertTriangle size={9} /> Action required
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-extrabold text-zinc-950 truncate">
                        {proposal?.project_summary?.split(".")[0] || `${p.industry} project`}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-zinc-500">
                        <span className="font-mono">{p.project_id}</span>
                        <span>·</span>
                        <span>{p.industry}</span>
                        <span>·</span>
                        <span>{p.delivery_weeks}-wk delivery</span>
                        {p.due_date && (
                          <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-700 font-bold" : "text-zinc-500"}`}>
                            <Calendar size={11} /> Due {fmtDate(p.due_date)}
                          </span>
                        )}
                      </div>
                      {p.preview_url && (
                        <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                          <ExternalLink size={10} /> Live preview ready
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      {price && <div className="text-2xl font-extrabold text-zinc-950">{fmt(price, p.currency)}</div>}
                      <div className="text-xs text-zinc-500 inline-flex items-center gap-1 justify-end mt-1">
                        Open <ArrowRight size={11} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
